import * as React from 'react';
import { useHistory } from 'react-router-dom'; // react-router-dom v5 compatible
import ErrorDialog from './ErrorDialog';

interface ErrorDialogOptions {
    message?: string;
    title?: string;
    navigateOnClose?: string;
    reloadOnNavigation?: boolean;
}

interface ErrorDialogContextType {
    showError: (options: ErrorDialogOptions) => void;
}

const ErrorDialogContext = React.createContext<ErrorDialogContextType | undefined>(undefined);

export const ErrorDialogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const history = useHistory();

    const [dialogState, setDialogState] = React.useState<{ open: boolean; options: ErrorDialogOptions | null }>({
        open: false,
        options: null,
    });

    const handleClose = React.useCallback(() => {
        setDialogState((prevState) => {
            if (prevState.options?.navigateOnClose) {
                if (prevState.options.reloadOnNavigation) {
                    window.location.href = prevState.options.navigateOnClose;
                } else {
                    history.push(prevState.options.navigateOnClose);
                }
            }
            return { open: false, options: null };
        });
    }, [history]);

    const showErrorDialog = React.useCallback((options: ErrorDialogOptions) => {
        setDialogState({ open: true, options });
    }, []);

    return (
        <ErrorDialogContext.Provider value={{ showError: showErrorDialog }}>
            {children}
            {/* Only render ErrorDialog when open is true */}
            {dialogState.open && (
                <ErrorDialog
                    errorTitle={dialogState.options?.title}
                    errorMsg={dialogState.options?.message}
                    onClose={handleClose}
                />
            )}
        </ErrorDialogContext.Provider>
    );
};

export const useErrorDialog = (): ErrorDialogContextType => {
    const context = React.useContext(ErrorDialogContext);
    if (!context) {
        throw new Error('useErrorDialog must be used within an ErrorDialogProvider');
    }
    return context;
};
